import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from 'lucide-react';
import { FirestoreLocation as Location } from '@/types/firestore';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GeoPoint, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAppSelector } from '@/store/hooks';
import { selectSelectedEntity, selectEditorOperationLoading } from '@/store/slices/editorSlice';

// Custom type for location with coordinates as a plain object during editing
interface EditableLocationInternal extends Omit<Location, 'coordinates' | 'id'> {
  id?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  googlePlaceId?: string;
}

interface LocationEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (location: Partial<Location>) => Promise<void>;
}

export const LocationEditDialog: React.FC<LocationEditDialogProps> = ({
  open,
  onOpenChange,
  onSave
}) => {
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const isSaveLoading = useAppSelector(selectEditorOperationLoading) === 'pending';

  const [editingLocation, setEditingLocation] = useState<Partial<EditableLocationInternal> | null>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isDialogCurrentlySaving, setIsDialogCurrentlySaving] = useState(false);

  useEffect(() => {
    const fetchLocationDetails = async () => {
      if (open && selectedEntity?.type === 'location' && selectedEntity.id) {
        setIsFetchingDetails(true);
        setFetchError(null);
        setEditingLocation(null);
        try {
          const locationDocRef = doc(db, 'locations', selectedEntity.id);
          const docSnap = await getDoc(locationDocRef);
          if (docSnap.exists()) {
            const fetchedData = docSnap.data() as Location;
            const formattedLocation: Partial<EditableLocationInternal> = { ...fetchedData, id: docSnap.id };
            if (fetchedData.coordinates instanceof GeoPoint) {
              formattedLocation.coordinates = {
                latitude: fetchedData.coordinates.latitude,
                longitude: fetchedData.coordinates.longitude
              };
            }
            setEditingLocation(formattedLocation);
          } else {
            setFetchError("Location not found.");
            console.error("Error: Location document not found for ID:", selectedEntity.id);
          }
        } catch (error) {
          console.error("Error fetching location details:", error);
          setFetchError("Failed to load location details.");
        } finally {
          setIsFetchingDetails(false);
        }
      } else if (!open) {
        setEditingLocation(null);
        setFetchError(null);
      }
    };

    fetchLocationDetails();

    if (!open) {
      setIsDialogCurrentlySaving(false); // Reset local saving state when dialog is closed
    }
  }, [open, selectedEntity]);
  
  const handleOpenChange = (newOpenState: boolean) => {
    if (!newOpenState) {
      // IMPORTANT: Do not re-introduce manual document.body.style.pointerEvents manipulation here.
      // The Radix UI Dialog component handles modality and pointer events automatically.
      // Manually changing pointerEvents here, especially within a setTimeout,
      // can lead to the page becoming unresponsive after the dialog closes.
    }
    onOpenChange(newOpenState);
  };

  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    if (open) {
      window.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onOpenChange]);

  const handleFieldChange = (field: string, value: any) => {
    setEditingLocation((prev) => prev ? { ...prev, [field]: value } : null);
  };

  const handleCoordinatesChange = (type: 'latitude' | 'longitude', value: string) => {
    const numValue = parseFloat(value);
    if (value === '') {
        setEditingLocation((prev) => {
            if (!prev) return null;
            const newCoords = { ...(prev.coordinates || { latitude: 0, longitude: 0 }) };
            if (type === 'latitude') delete (newCoords as any).latitude;
            if (type === 'longitude') delete (newCoords as any).longitude;
            return { ...prev, coordinates: newCoords as any };
        });
        return;
    }
    if (isNaN(numValue)) return;
    
    setEditingLocation((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        coordinates: {
          ...(prev.coordinates || { latitude: 0, longitude: 0 }),
          [type]: numValue
        }
      };
    });
  };

  const handleSave = async (): Promise<void> => {
    if (!editingLocation || isDialogCurrentlySaving || isSaveLoading) return;
    
    setIsDialogCurrentlySaving(true);
    const locationToSave: Partial<Location> = {};
    Object.keys(editingLocation).forEach(key => {
      if (key !== 'coordinates' && key !== 'id') {
        (locationToSave as any)[key] = (editingLocation as any)[key];
      }
    });
    
    if (editingLocation.googlePlaceId) {
      locationToSave.googlePlaceId = editingLocation.googlePlaceId;
    } else {
      delete locationToSave.googlePlaceId;
    }
    
    if (editingLocation.coordinates && 
        typeof editingLocation.coordinates.latitude === 'number' && 
        typeof editingLocation.coordinates.longitude === 'number') {
      locationToSave.coordinates = new GeoPoint(
        editingLocation.coordinates.latitude,
        editingLocation.coordinates.longitude
      );
    } else if (editingLocation.coordinates && 
               (editingLocation.coordinates.latitude === undefined || editingLocation.coordinates.longitude === undefined)) {
    }

    try {
      await onSave(locationToSave);
      // If onSave is successful, the parent component is expected to call onOpenChange(false),
      // which will trigger the useEffect listening to 'open' to set isDialogCurrentlySaving to false.
    } catch (error) {
      console.error('Error saving location:', error);
      setIsDialogCurrentlySaving(false); // Reset on error so user can try again or close
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!open) return null;

  if (isFetchingDetails) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px] flex items-center justify-center">
          <DialogHeader>
            <DialogTitle>Loading Data</DialogTitle>
            <DialogDescription>
              Please wait while the location details are being loaded.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>Loading location details...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (fetchError) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>{fetchError}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!editingLocation && selectedEntity?.type === 'location') {
     return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>Could not load location data to edit.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!editingLocation) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Location</DialogTitle>
          <DialogDescription>
            Make changes to the location information below.
          </DialogDescription>
        </DialogHeader>
        
        <div className={`grid gap-4 py-4 ${isDialogCurrentlySaving ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">Name</Label>
            <Input 
              id="name" 
              value={editingLocation.name || ''} 
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="col-span-3"
              disabled={isDialogCurrentlySaving}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="address" className="text-right">Address</Label>
            <Input 
              id="address" 
              value={editingLocation.address || ''} 
              onChange={(e) => handleFieldChange('address', e.target.value)}
              className="col-span-3"
              disabled={isDialogCurrentlySaving}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="url" className="text-right">URL</Label>
            <Input 
              id="url" 
              value={editingLocation.url || ''} 
              onChange={(e) => handleFieldChange('url', e.target.value)}
              className="col-span-3"
              disabled={isDialogCurrentlySaving}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="locationType" className="text-right">Type</Label>
            <Select 
              value={editingLocation.locationType || ''} 
              onValueChange={(value) => handleFieldChange('locationType', value)}
              disabled={isDialogCurrentlySaving}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="restaurant">Restaurant</SelectItem>
                <SelectItem value="shop">Shop</SelectItem>
                <SelectItem value="brewery">Brewery</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="latitude" className="text-right">Latitude</Label>
            <Input 
              id="latitude" 
              value={editingLocation.coordinates?.latitude ?? ''}
              onChange={(e) => handleCoordinatesChange('latitude', e.target.value)}
              className="col-span-3"
              type="number"
              step="any"
              disabled={isDialogCurrentlySaving}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="longitude" className="text-right">Longitude</Label>
            <Input 
              id="longitude" 
              value={editingLocation.coordinates?.longitude ?? ''}
              onChange={(e) => handleCoordinatesChange('longitude', e.target.value)}
              className="col-span-3"
              type="number"
              step="any"
              disabled={isDialogCurrentlySaving}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="googlePlaceId" className="text-right whitespace-nowrap">Google Place ID</Label>
            <Input
              id="googlePlaceId"
              value={editingLocation.googlePlaceId || ''}
              onChange={(e) => handleFieldChange('googlePlaceId', e.target.value)}
              className="col-span-3"
              disabled={isDialogCurrentlySaving}
            />
          </div>
        </div>
        
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={handleCancel} disabled={isDialogCurrentlySaving}>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isDialogCurrentlySaving || isSaveLoading || isFetchingDetails}>
            {isDialogCurrentlySaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 